import React from 'react';
import { useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useMedia from 'utils/hooks/useMedia';
import useRedirect from 'utils/hooks/useRedirect';
import { setFileUploaderState } from 'store/entities/submissions';
import { sanitizeHtml } from 'utils/stringUtils';

import isEqual from 'date-fns/isEqual';
import { formatDateTime } from 'utils/dateUtils';

import { toggleBlackBoxFileUploader } from 'store/entities/blackbox';
import { Column, Row } from 'components/UIComponents/Layout';
import Button from 'components/UIComponents/Button';
import Icon from '@mdi/react';
import { mdiChevronRightCircle, mdiInformationOutline, mdiMessageText, mdiUpload } from '@mdi/js';
import StatusDecal from './StatusDecal';
import Tooltip from 'components/UIComponents/Tooltip';

const ListItem = ({ data }) => {
  const { institutionId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userDetails } = useSelector(state => state.auth);

  const redirect = useRedirect();

  const {
    status,
    id,
    reporter,
    dueDate,
    initialDueDate,
    timeSeriesName,
    tableName,
    obligationDescription,
    institutionName,
    lastMessage,
    fileType,
    mainUser,
  } = data;
  const isBlackBoxItem = status === 'bb';
  const isSmallerThanWidescreen = useMedia(
    ['(max-width: 1215px)', '(min-width: 1px)'],
    [true, false],
    false
  );

  const uploadDisabled =
    status === 'ARRIVED' ||
    status === 'IN_PROGRESS' ||
    status === 'OK' ||
    status === 'CLOSED';

  const addFile = () =>
    isBlackBoxItem
      ? dispatch(toggleBlackBoxFileUploader(true))
      : dispatch(
          setFileUploaderState({
            submissionId: id,
            tableName,
            fileType,
            dueDate,
            mainUser,
            reporter,
            fileUploaderOpen: true,
          })
        );

  const isMyObligationsPage = !institutionId;

  return (
    <Row
      bordered={!isSmallerThanWidescreen}
      hasBottomBorder={isSmallerThanWidescreen}
      className="list-item"
    >
      <Column
        tablet={{ size: 12 }}
        desktop={{ size: 12 }}
        widescreen={{ size: 6 }}
        onClick={() => !isBlackBoxItem && redirect(`/submission/${id}`)}
        style={{ cursor: !isBlackBoxItem ? 'pointer' : 'auto' }}
      >
        <Row>
          <Column
            tablet={{ size: 12 }}
            widescreen={{ size: 3 }}
            fullhd={{ size: 2 }}
          >
            <StatusDecal date={new Date(dueDate)} status={status} />
          </Column>
          <Column
            tablet={{ size: 12 }}
            widescreen={{ size: 9 }}
            className="list-item__desc"
          >
            <h2>
              <strong>
                {isBlackBoxItem
                  ? t('datagate.external.submissions.title.blackBox')
                  : tableName}
              </strong>
            </h2>
            <p>
              {isBlackBoxItem ? (
                <Trans
                  components={{
                    mailTo: <a href={'mailto: stat@stat.stat'}> </a>,
                  }}
                >
                  datagate.external.submissions.description.blackBox
                </Trans>
              ) : (
                obligationDescription
              )}{' '}
              {!isBlackBoxItem && (
                <Button
                  inverted
                  color="primary"
                  id={`openObligationDetail${id}`}
                >
                  <Icon path={mdiChevronRightCircle} size={1} />
                  <strong>
                    {t('datagate.external.submissions.button.forward')}
                  </strong>
                </Button>
              )}
            </p>
          </Column>
        </Row>
      </Column>
      <Column
        mobile={{ size: 12 }}
        tablet={{ size: isBlackBoxItem ? 12 : 6 }}
        desktop={{ size: isBlackBoxItem ? 12 : 6 }}
        widescreen={{ size: 3 }}
        fullhd={{ size: 2 }}
        className="list-item__center"
      >
        <Button
          outlined
          id={`addFile${id}`}
          fullwidth={isSmallerThanWidescreen}
          disabled={uploadDisabled}
          onClick={addFile}
        >
          <Icon path={mdiUpload} size={1} />
          {t('datagate.external.submissions.button.addFile')}
        </Button>
        {isBlackBoxItem ? (
          <p className="list-item__institution">
            <span>{t('datagate.external.submissions.label.reporter')}:</span>{' '}
            {`${userDetails?.firstName} ${userDetails?.lastName}`}
          </p>
        ) : (
          <p className="list-item__institution">
            <span>
              {t(
                `datagate.external.submissions.label.${
                  isMyObligationsPage ? 'institution' : 'reporter'
                }`
              )}
              :
            </span>{' '}
            {isMyObligationsPage
              ? institutionName
              : reporter
              ? `${reporter?.firstName} ${reporter?.lastName}`
              : t('datagate.external.submissions.label.reporter.unassigned')}
          </p>
        )}
        {timeSeriesName && (
          <div className="list-item__details">
            <span style={{display: 'inline-flex', alignItems: 'baseline', gap: '4px'}}>
                {t('datagate.external.submissions.label.timeSeries')}:
              <Tooltip
                position="top"
                html={t('datagate.external.submissions.tooltip.timeSeries')}>
                  <Icon path={mdiInformationOutline} size={0.75}/>
                </Tooltip>
              </span>{' '}
            {`${timeSeriesName}`}
          </div>
        )}
        {initialDueDate && !isEqual(new Date(dueDate), new Date(initialDueDate)) && (
          <div className="list-item__details">
            <span>{t('datagate.external.submissions.label.initialDueDate')}:</span>{' '}
            {`${formatDateTime(initialDueDate)}`}
          </div>
        )}
      </Column>
      <Column
        mobile={{ size: 12 }}
        tablet={{ size: 6 }}
        widescreen={{ size: 3 }}
        fullhd={{ size: 4 }}
        breakpoint="tablet"
      >
        {!isBlackBoxItem && (
          <>
            <div className="flex-center-y spacing pb-3">
              <h2 className="pb-0 is-uppercase">
                <strong>
                  {t('datagate.external.submissions.subtitle.messages')}
                </strong>
              </h2>
              {data.containsMessages && (
                <Tooltip html={<div>{t('datagate.external.submissions.contains.messages')}</div>}>
                  <Icon path={mdiMessageText} size={1}/>
                </Tooltip>
              )}
            </div>
            {lastMessage && (
              <p
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(lastMessage.content),
                }}
              />
            )}
          </>
        )}
      </Column>
    </Row>
  );
};

export default ListItem;
